import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField, Grid, Typography, Autocomplete } from "@mui/material";
import EmployerViewController from "../../../redux/actions/EmployerApiAction";
import { getUserInfoFromToken } from "../../../utils/authUtils";

const attendanceTypeOptions = [
  { label: "Biometric", value: "biometric" },
  { label: "Manual", value: "manual" },
  { label: "App Based", value: "app_based" },
];

const LeaveInformationTab = ({ formik }) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    formik;
  const dispatch = useDispatch();
  const { getEmployer, getAllEmployeeEmployer } = EmployerViewController();
  const { employers } = useSelector((state) => state.employer);
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  useEffect(() => {
    if (!userType) return;
    if (userType === "Admin") {
      dispatch(getEmployer());
    } else {
      dispatch(getAllEmployeeEmployer());
    }
  }, [dispatch, userType]);

  const leaveApprovalManagerOptions = useMemo(() => {
    const source =
      userType === "Admin" && values.company_id
        ? employers.filter(
            (emp) =>
              String(emp.company?.id ?? emp.company) ===
              String(values.company_id)
          )
        : employers;

    return source.map((emp) => ({
      label: `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
      value: emp.id,
    }));
  }, [userType, employers, values.company_id]);


  const overtimeEligibleOptions = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];


  return (
    <Grid container spacing={2.5}>
      {/* Leave Approval Managers (multi-select) */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          multiple
          fullWidth
          options={leaveApprovalManagerOptions}
          getOptionLabel={(option) => option.label}
          value={
            leaveApprovalManagerOptions.filter((opt) =>
              values.leave_approval_managers?.includes(opt.value)
            )
          }
          onChange={(event, newValue) => {
            const selectedIds = newValue.map((opt) => opt.value);
            setFieldValue("leave_approval_managers", selectedIds);
          }}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Leave Approval Managers"
              name="leave_approval_managers"
              error={
                touched.leave_approval_managers &&
                Boolean(errors.leave_approval_managers)
              }
              helperText={
                touched.leave_approval_managers && errors.leave_approval_managers
              }
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 4 * 48, // limit visible items to 4
              overflowY: "auto",
            },
          }}
        />
      </Grid>

      {/* Attendance Type */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          fullWidth
          options={attendanceTypeOptions}
          getOptionLabel={(option) => option.label}
          value={
            attendanceTypeOptions.find(
              (opt) => opt.value === values.attendance_type
            ) || null
          }
          onChange={(event, newValue) => {
            setFieldValue("attendance_type", newValue ? newValue.value : "");
          }}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Attendance Type"
              name="attendance_type"
              error={touched.attendance_type && Boolean(errors.attendance_type)}
              helperText={touched.attendance_type && errors.attendance_type}
              sx={{
                "& .MuiInputBase-input": {
                  cursor: "text", // shows text cursor when focused
                },
              }}
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 4 * 48,
              overflowY: "auto",
            },
          }}
        />
      </Grid>


      {/* Overtime Eligible */}
      <Grid item xs={12} sm={6}>
      <Autocomplete
        fullWidth
        options={overtimeEligibleOptions}
        getOptionLabel={(option) => option.label}
        value={
          overtimeEligibleOptions.find(
            (opt) => opt.value === values.overtime_eligible
          ) || null
        }
        onChange={(event, newValue) => {
          setFieldValue("overtime_eligible", newValue ? newValue.value : "");
        }}
        onBlur={handleBlur}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Overtime Eligible"
            name="overtime_eligible"
            error={touched.overtime_eligible && Boolean(errors.overtime_eligible)}
            helperText={touched.overtime_eligible && errors.overtime_eligible}
            sx={{
              "& .MuiInputBase-input": {
                cursor: "text",
              },
            }}
          />
        )}
      
      />
    </Grid>


      {/* Leave Year */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Leave Year"
          name="leave_year"
          value={values.leave_year || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.leave_year && Boolean(errors.leave_year)}
          helperText={touched.leave_year && errors.leave_year}
          inputProps={{ min: 1900, max: 2100 }}
        />
      </Grid>

      {/* Leave Balance Section */}
      <Grid item xs={12}>
        <Typography variant="h6" mt={3} gutterBottom>
          Annual Leave Details
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Casual Leave"
          name="casual_leave_balance"
          value={values.casual_leave_balance}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={touched.casual_leave_balance && Boolean(errors.casual_leave_balance)}
          helperText={touched.casual_leave_balance && errors.casual_leave_balance}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Sick Leave"
          name="sick_leave_balance"
          value={values.sick_leave_balance}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={touched.sick_leave_balance && Boolean(errors.sick_leave_balance)}
          helperText={touched.sick_leave_balance && errors.sick_leave_balance}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Maternity Leave"
          name="maternity_leave_balance"
          value={values.maternity_leave_balance}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={touched.maternity_leave_balance && Boolean(errors.maternity_leave_balance)}
          helperText={touched.maternity_leave_balance && errors.maternity_leave_balance}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Marriage Leave"
          name="marriage_leave_balance"
          value={values.marriage_leave_balance}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.marriage_leave_balance && Boolean(errors.marriage_leave_balance)}
          helperText={touched.marriage_leave_balance&& errors.marriage_leave_balance}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Compensatory Off"
          name="compensatory_off_leave_balance"
          value={values.compensatory_off_leave_balance}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={
            touched.compensatory_off_leave_balance &&
            Boolean(errors.compensatory_off_leave_balance)
          }
          helperText={touched.compensatory_off_leave_balance && errors.compensatory_off_leave_balance}
          inputProps={{ min: 0 }}
        />
      </Grid>

      {/* Unpaid Leave Balance */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Unpaid Leave Balance"
          name="unpaid_leave_balance"
          value={values.unpaid_leave_balance }
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.unpaid_leave_balance && Boolean(errors.unpaid_leave_balance)}
          helperText={touched.unpaid_leave_balance && errors.unpaid_leave_balance}
          inputProps={{ min: 0 }}
        />
      </Grid>
    </Grid>
  );
};

export default LeaveInformationTab;
